import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DollarSign, X } from "lucide-react";
import CustomDatePicker from "../../common/CustomDatePicker";
import { useModalStyles } from "../../common/useModalStyles";
import { Controller, useForm } from "react-hook-form";
import { ProductItemPriceInfo, initialValuesOfProductItemPrice } from "../../../redux/slice/product/createProductSlice";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import dayjs, { Dayjs } from "dayjs";
import { useCommonReducer } from "../../../redux/slice/commonSlice";
import { AddCircleOutlineOutlined, DeleteOutlineOutlined, EditOutlined, ErrorOutlineRounded, ModeOutlined } from "@mui/icons-material";
import CustomTextField from "../../common/CustomTextField";
import CustomIconButton from "../../common/CustomIconButton";
import Button from "../../common/Button";
import Table from "../../common/CustomTable";
import CustomCurrencyField from "../../common/CustomCurrencyField";
import OptionsPopup, { OptionPopupObjectProps } from "../../common/OptionsPopup";
import { FieldLabel } from "../../../utils/Utility";
import { TIER_PRICE_CODETABLE } from "../../../constants/applicationConstants";
import { CodeTable, getCodeTableValuesByTableName, useCodetablesSlice } from "../../../redux/slice/codetableSlice";

interface ProductItemPriceProps {
  open: boolean;
  onClose: () => void;
  addProductItemPrice: (productItemPrice: ProductItemPriceInfo) => void;
  productItemPriceObject: ProductItemPriceInfo;
  editPricing: boolean;
  editProduct: boolean;
  rowIndex: number | null;
  inactiveOnly: boolean;
  productItemPriceList: ProductItemPriceInfo[];
  canManageTierPrice?: boolean;
}

let erroredFields: string[] = [];

type TierPriceRow = { tier: string; price: string; tierLabel?: string };

const rowsFromTierPrices = (
  tierPrices: Record<string, string | number> | null | undefined,
  tierKeys: string[]
): TierPriceRow[] => {
  if (!tierPrices) {
    return [];
  }
  const keys = tierKeys.length > 0 ? tierKeys : Object.keys(tierPrices);
  return keys.filter((tier) => {
    const price = tierPrices[tier];
    return price !== undefined && price !== null && String(price).trim() !== "";
  }).map((tier) => ({ tier, price: String(tierPrices[tier]) }));
};

const tierPricesFromRows = (rows: TierPriceRow[]): Record<string, number> => {
  const tierPrices: Record<string, number> = {};
  rows.forEach((row) => {
    const price = Number(row.price);
    if (!Number.isNaN(price)) {
      tierPrices[row.tier] = price;
    }
  });
  return tierPrices;
};

const ProductItemPricePricePopup: React.FC<ProductItemPriceProps> = ({
  open,
  onClose,
  addProductItemPrice,
  productItemPriceObject,
  editPricing,
  editProduct,
  rowIndex,
  inactiveOnly,
  productItemPriceList,
  canManageTierPrice = false,
}) => {
  const showInlineErrorMinLength: number = 60;
  const customTopErrorString: string = "-custom top error";
  const [submitAction, setSubmitAction] = useState<string>();
  const { control, handleSubmit, reset, setError, watch, getValues, formState } = useForm<ProductItemPriceInfo>({ defaultValues: initialValuesOfProductItemPrice });
  const [showTierPopup, setShowTierPopup] = useState(false);
  const [editingTier, setEditingTier] = useState<string | null>(null);
  const [tierRows, setTierRows] = useState<TierPriceRow[]>([]);
  const tierForm = useForm<{ tier: string; price: string }>({ defaultValues: { tier: "", price: "" } });
  const { errors } = formState;
  const [showOptionPopup, setShowOptionsPopup] = useState<OptionPopupObjectProps>({ message: "", show: false });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";
  const {
    tokens,
    paperSx,
    headerSx,
    bodySx,
    footerSx,
    iconBoxSx,
    primaryBtnSx,
    cancelBtnSx,
    dropdownWrapSx,
    inputFieldSx,
    scrollAreaSx,
  } = useModalStyles();

  const { macInfoList } = useCommonReducer();
  const { allCodeTablesValuesMap } = useCodetablesSlice();
  const tierCodeRows = useMemo(() => {
    const values = getCodeTableValuesByTableName(allCodeTablesValuesMap as any, TIER_PRICE_CODETABLE);
    return Array.isArray(values) ? values : [];
  }, [allCodeTablesValuesMap]);
  const tierKeys = useMemo(() => tierCodeRows.map((row: CodeTable) => row.keyName).filter(Boolean), [tierCodeRows]);
  const tierLabel = (key: string) => tierCodeRows.find((row) => row.keyName === key)?.displayValue || key;

  const errorMessages = useMemo(() => {
    let errorMsgs: JSX.Element[] = [];
    if (errors) {
      let objKeys: string[] = Object.keys(errors);
      let errMsg: string | undefined = undefined;
      if (objKeys && objKeys.length > 0) {
        objKeys.map((key) => {
          // @ts-ignore
          errMsg = errors[key].message;
          if (errMsg && (errMsg.length >= showInlineErrorMinLength || errMsg.includes(customTopErrorString))) {
            errMsg = errMsg.replace(customTopErrorString, "");
            errorMsgs.push(<Box sx={{ display: 'flex', justifyContent: 'flex-start', color: 'error.main' }} >
              <Box sx={{ pr: 0.5, }} ><ErrorOutlineRounded /></Box>
              <Box>{errMsg}</Box>
            </Box>);
          }
        });
      }
    }
    return errorMsgs;
  }, [formState]);

  const mac1Options = useMemo(() => {
    let options: { label: string, value: any }[] = [];
    macInfoList?.map((option) => {
      options.push({ label: option.macName, value: option.macId })
    })
    return options;
  }, [macInfoList]);

  const options = useMemo(() => {
    return [
      { label: "MAC", value: "MAC" },
      { label: "National", value: "NATIONAL" },
    ]
  }, []);

  useEffect(() => {
    if (open) {
      let Obj: ProductItemPriceInfo = {
        ...productItemPriceObject,
        inactiveDate: productItemPriceObject.inactiveDate ? dayjs(productItemPriceObject.inactiveDate) : null,
        effectiveDate: productItemPriceObject.effectiveDate ? dayjs(productItemPriceObject.effectiveDate) : null,
      }
      reset(Obj)
      const rows = rowsFromTierPrices(productItemPriceObject.tierPrices, tierKeys);
      setTierRows(rows);
      setShowTierPopup(false);
      setEditingTier(null);
      tierForm.reset({ tier: "", price: "" });
    }
    if (!open) {
      setDefaultValues();
          }
  }, [open, tierKeys]);

  const validateOthers = useCallback(() => {

    if (inactiveOnly) {
      return validateInactivate();
    } 

    if (!productItemPriceList || productItemPriceList.length === 0) {
      return true;
    }

    let priceList: ProductItemPriceInfo[] = productItemPriceList;
    if (editPricing) {
      priceList = productItemPriceList.filter((item: ProductItemPriceInfo, index: number) => {
        if (index !== rowIndex) {
          return true;
        }
        return false;
      });
    }

    let fileType: string = getValues('priceType');
    let macId: number = getValues('macId');
    let effectiveDateLocal: Dayjs = dayjs(getValues('effectiveDate'));

    if (editProduct) {
      let currentDate: Dayjs = dayjs(dayjs().format('MM/DD/YYYY'));
      // let sameTypePriceMap: Map<string | number, ProductItemPriceInfo[]> = new Map<string, ProductItemPriceInfo[]>();
      // let sameTypePriceList: ProductItemPriceInfo[] | undefined = [];
      // let mapKey: string | number = "";

      for (let i : number = 0; i < priceList.length; i++) {
        // if (priceList[i].priceType === "NATIONAL") {
        //   mapKey = "NATIONAL";
        // } else {
        //   mapKey = priceList[i].macId;
        // }

        // if (sameTypePriceMap.has(mapKey)) {
        //   sameTypePriceList = sameTypePriceMap.get(mapKey);
        //   sameTypePriceList?.push(priceList[i]);
        // } else {
        //   sameTypePriceList = [];
        //   sameTypePriceList.push(priceList[i]);
        //   sameTypePriceMap.set(mapKey, sameTypePriceList);
        // }

        // if (!dayjs(priceList[i].inactiveDate).isValid()
        //   && (fileType !== priceList[i].priceType || fileType === "NATIONAL" || (fileType === "MAC" && macId === priceList[i].macId))) {
        //   let macName = fileType === "MAC" ? macInfoList?.find(option => option.macId === priceList[i].macId)?.macName : "";
        //   if (currentDate.isBefore(dayjs(priceList[i].effectiveDate))) {
        //     return priceList[i].priceType === "NATIONAL"
        //       ? "A future active National pricing is exists. Please delete it or set a suitable inactive date before adding a new pricing." + customTopErrorString
        //       : fileType === "NATIONAL" ? "Future active Local pricing/pricings is/are exists. Please delete it/them or set (a) suitable inactive date(s) before adding a new pricing." + customTopErrorString
        //         : "A future active " + macName + " pricing is exists. Please delete it or set a suitable inactive date before adding a new pricing." + customTopErrorString;
        //   }
        //    else {
        //     return priceList[i].priceType === "NATIONAL"
        //       ? "An active National pricing is exists. Please set a suitable inactive date before adding a new pricing." + customTopErrorString
        //       : fileType === "NATIONAL" ? "An active Local pricing/pricings is/are exists. Please set (a) suitable inactive date(s) before adding a new pricing." + customTopErrorString
        //         : "An active " + macName + " pricing is exists. Please set a suitable inactive date before adding a new pricing." + customTopErrorString;
        //   }
        // }

      }

      // for (let i : number = 0; i < priceList.length; i++) {
      //   if (dayjs(priceList[i].inactiveDate).isValid() && effectiveDateLocal.isValid()
      //     && (effectiveDateLocal.isSame(dayjs(priceList[i].inactiveDate)) || effectiveDateLocal.isBefore(dayjs(priceList[i].inactiveDate)))
      //     && (fileType === "NATIONAL" || priceList[i].priceType === "NATIONAL" || macId === priceList[i].macId)) {
      //     return "Entered effective date is conflicted with a active period of a existing pricing." + customTopErrorString;
      //   }
      // }

    } else {
      for (let i : number = 0; i < priceList.length; i++) {
        if ("MAC" === priceList[i].priceType && fileType === "NATIONAL") {
          erroredFields.push("priceType");
          return "Mac pricing is/are already exists." + customTopErrorString;
        } else if (fileType === priceList[i].priceType && fileType === "NATIONAL") {
          erroredFields.push("priceType");
          return "A National pricing is already exists." + customTopErrorString;
        } else if (macId === priceList[i].macId) {
          erroredFields.push("macId");
          return "This MAC pricing is already exists." + customTopErrorString;
        }
      }
    }
    return true;
  }, [productItemPriceList, watch('priceType'), watch('macId'), watch('effectiveDate')]);

  const validateInactivate = useCallback(() => {
    let fileType: string = getValues('priceType');
    let macId: number = getValues('macId');
    let effectiveDateLocal: Dayjs = dayjs(getValues('effectiveDate'));
    let inactiveDateLocal: Dayjs = dayjs(getValues('inactiveDate'));

    let priceList: ProductItemPriceInfo[] = productItemPriceList;
    if (editPricing) {
      priceList = productItemPriceList.filter((item: ProductItemPriceInfo, index: number) => {
        if (effectiveDateLocal.isBefore(dayjs(item.effectiveDate))) {
          return true;
        }
        return false;
      });
    }

    for (let i : number = 0; i < priceList.length; i++) {
      if (inactiveDateLocal.isValid()
        && (inactiveDateLocal.isSame(dayjs(priceList[i].effectiveDate)) || inactiveDateLocal.isAfter(dayjs(priceList[i].effectiveDate)))
        && (fileType === "NATIONAL" || priceList[i].priceType === "NATIONAL" || macId === priceList[i].macId)) {
        return "Entered inactive date is conflicted with a active period of a existing pricing." + customTopErrorString;
      }
    }

  }, [productItemPriceList, watch('priceType'), watch('macId'), watch('inactiveDate')]);

  const validateEffectiveDate = useCallback((value: string | number | Date | dayjs.Dayjs | null | undefined) => {
    if (!dayjs(value, "MM/DD/YYYY", true).isValid()) {
      return "Invalid date format."
    }

    return true;
  }, [productItemPriceList, watch('effectiveDate'), watch('macId')]);

  const validateInactiveDate = useCallback((value: string | number | Date | dayjs.Dayjs | null | undefined) => {
    if (!dayjs(value, "MM/DD/YYYY", true).isValid()) {
      return "Invalid date format."
    }

    if (dayjs(value, "MM/DD/YYYY", true).isValid()
      && dayjs(getValues("effectiveDate"), "MM/DD/YYYY", true).isValid()
      && dayjs(getValues("effectiveDate"), "MM/DD/YYYY", true).isAfter(dayjs(value, "MM/DD/YYYY", true))) {
      return "Inactive date should be a past date or euqal to effective date."
    }

    return true;
  }, [productItemPriceList, watch('inactiveDate'), watch('macId')]);

  const validateOptionalInactiveDate = useCallback((value: string | number | Date | dayjs.Dayjs | null | undefined) => {
    if (value !== null) {
      if (!dayjs(value, "MM/DD/YYYY", true).isValid()) {
          return "Invalid date format.";
      }
    }

    if (dayjs(value, "MM/DD/YYYY", true).isValid()
      && dayjs(getValues("effectiveDate"), "MM/DD/YYYY", true).isValid()
      && dayjs(getValues("effectiveDate"), "MM/DD/YYYY", true).isAfter(dayjs(value, "MM/DD/YYYY", true))) {
      return "The inactive date should be the same as or after the effective date."
    }

    return true;
  }, [productItemPriceList, watch('inactiveDate'), watch('macId')]);

  const setDefaultValues = useCallback(() => {
    reset();
  }, []);
  const cancelOptionPopup = () => {
    setShowOptionsPopup({ message: "", show: false });
};
  const tierOptions = tierCodeRows
    .filter((row) => row.keyName === editingTier || !tierRows.some((item) => item.tier === row.keyName))
    .map((row) => ({ label: row.displayValue || row.keyName, value: row.keyName }));

  const openTierPopup = (row?: TierPriceRow) => {
    setEditingTier(row?.tier ?? null);
    setShowTierPopup(true);
  };

  useEffect(() => {
    if (!showTierPopup) {
      return;
    }
    const remaining = tierKeys.filter((tier) => !tierRows.some((item) => item.tier === tier));
    tierForm.reset({
      tier: editingTier ?? remaining[0] ?? "",
      price: tierRows.find((row) => row.tier === editingTier)?.price ?? "",
    });
  }, [showTierPopup, editingTier, tierKeys]);

  const closeTierPopup = () => {
    setShowTierPopup(false);
  };

  const saveTierRow = (data: { tier: string; price: string }) => {
    const price = (data.price ?? "").trim();
    if (!data.tier || !price) {
      return;
    }
    setTierRows((rows) => {
      const withoutEdited = rows.filter((row) => row.tier !== editingTier && row.tier !== data.tier);
      return [...withoutEdited, { tier: data.tier, price }];
    });
    setShowTierPopup(false);
  };

  const deleteTierRow = (tier: string) => {
    setShowOptionsPopup({
      message: `Are you sure you want to delete the ${tierLabel(tier)} tier price?`,
      show: true,
      variant: "confirm",
      buttons: [
        {
          name: "Yes",
          color: "primary",
          onClick: () => {
            setTierRows((rows) => rows.filter((row) => row.tier !== tier));
            setShowOptionsPopup({ message: "", show: false });
          },
        },
        { name: "No", color: "error", onClick: cancelOptionPopup },
      ],
    });
  };

  const onSubmit = useCallback((data: ProductItemPriceInfo) => {
    if(submitAction==="AddingNewProductPricing"){
      let obj: ProductItemPriceInfo = {
        ...data,
        tierPrices: canManageTierPrice ? tierPricesFromRows(tierRows) : {},
        effectiveDate: data.effectiveDate ? dayjs(data.effectiveDate).format("MM/DD/YYYY") : "",
        inactiveDate: data.inactiveDate ? dayjs(data.inactiveDate).format("MM/DD/YYYY") : ""
      }
     
     if(!obj?.inactiveDate){
      // CS-1288 Changes the popUp messgage.
      const effectiveDateMinusOne = dayjs(data.effectiveDate).subtract(1, "day");
      const yesterday = dayjs().subtract(1, "day");

      const inactivateDate = effectiveDateMinusOne.isAfter(yesterday) 
        ? effectiveDateMinusOne 
        : yesterday;
      setShowOptionsPopup({
        message: `All current agreements will be inactivated as of ${effectiveDateMinusOne.format("MM/DD/YYYY")}, Do you want to proceed?`,//CS-1337
        show: true,
        variant: "confirm",
        buttons: [
          { name: "Confirm", color: "primary", onClick: () => addProductItemPrice(obj) },
          { name: "Cancel", color: "secondary", onClick: cancelOptionPopup },
        ],
      });
    }else{
      addProductItemPrice(obj) 
    }
    }
   
  }, [open, submitAction, tierRows, canManageTierPrice]);

  const onError = (_errors: any, e: any) => {
    erroredFields.forEach((fieldName: string, index: number) => {
      // @ts-ignore
      setError(fieldName, {});
    });
    erroredFields = [];
  };

  const validateAmount = (value: string, message: string) => {
    let val: number = value !== "" ? parseFloat(value) : 0;
    if (val === 0 || val === 0.0) {
      return message;
    }
  }

  return (
    <>
     {showOptionPopup.show && (
                <OptionsPopup
                    open={showOptionPopup.show}
                    onClose={() => setShowOptionsPopup({ message: "", show: false })}
                    variant={showOptionPopup.variant}
                    message={<>{showOptionPopup.message}</>}
                    buttons={showOptionPopup.buttons}
                    title={showOptionPopup.title}
                    obj={showOptionPopup.obj}
                />
            )}
      <Dialog
        open={open}
        keepMounted
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        aria-describedby="product-item-price-dialog"
        onClick={(event) => event.stopPropagation()}
        PaperProps={{
          sx: {
            ...paperSx(isMobile),
            borderRadius: isMobile ? 0 : "8px",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: isDark ? "rgba(0,0,0,0.78)" : "rgba(15,23,42,0.5)",
            backdropFilter: "blur(6px)",
          },
        }}
      >
        <form onSubmit={handleSubmit(onSubmit, onError)}>
          <Box
            sx={{
              ...headerSx,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.75, flex: 1, minWidth: 0 }}>
              <Box sx={iconBoxSx("#14b8a6")}>
                <DollarSign size={22} color="#14b8a6" strokeWidth={1.8} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="h2"
                  fontWeight={700}
                  sx={{
                    fontSize: "1.25rem",
                    color: tokens.textPrimary,
                    lineHeight: 1.25,
                  }}
                >
                  {editPricing ? "Update product pricing" : "Add product pricing"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    color: isDark ? "#94a3b8" : "#64748b",
                    mt: 0.35,
                    fontWeight: 500,
                  }}
                >
                  {inactiveOnly
                    ? "Set an inactive date for this price row."
                    : "Enter allowable, list, and optional custom pricing with effective dates."}
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={onClose}
              size="small"
              aria-label="Close"
              sx={{
                color: isDark ? "#64748b" : "#637381",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
              }}
            >
              <X size={20} />
            </IconButton>
          </Box>

          <Box sx={bodySx}>
            {errorMessages.length > 0 && (
              <Box sx={{ pb: 1 }}>
                {errorMessages}
              </Box>
            )}

            {!inactiveOnly && (
              <Grid container spacing={2}>
                <Grid item md={6} xs={12}>
                  <Box>
                    <FieldLabel label="File Type" required tokens={tokens} />
                    <Box sx={dropdownWrapSx(!!errors.priceType)}>
                      <CustomDropdownField
                        label=""
                        name="priceType"
                        options={options}
                        control={control}
                        size="small"
                        showErrorMinLength={showInlineErrorMinLength}
                        rules={{
                          required: "File Type is required.",
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
                {(watch("priceType") ? getValues("priceType") === "MAC" : false) ? (
                  <Grid item md={6} xs={12}>
                    <Box>
                      <FieldLabel label="MAC" required tokens={tokens} />
                      <Box sx={dropdownWrapSx(!!errors.macId)}>
                        <CustomDropdownField
                          label=""
                          name="macId"
                          options={mac1Options}
                          control={control}
                          size="small"
                          showErrorMinLength={showInlineErrorMinLength}
                          rules={{
                            required: "MAC is required.",
                            validate: {
                              customValidation: (value: string) => {
                                if (!value) {
                                  return "MAC is required.";
                                }
                              },
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </Grid>
                ) : null}

                <Grid item md={6} xs={12}>
                  <Box>
                    <FieldLabel label="Allowable" required tokens={tokens} />
                    <Box sx={inputFieldSx(!!errors.allowableUnitPrice)}>
                      <CustomCurrencyField
                        label=""
                        controllerName="allowableUnitPrice"
                        control={control}
                        size="small"
                        showErrorMinLength={showInlineErrorMinLength}
                        rules={{
                          required: "Allowable is required.",
                          validate: {
                            validateAmount: (value: string) => {
                              return validateAmount(value, "Enter a valid Price.");
                            },
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>

                <Grid item md={6} xs={12}>
                  <Box>
                    <FieldLabel label="List Price" required tokens={tokens} />
                    <Box sx={inputFieldSx(!!errors.listPrice)}>
                      <CustomCurrencyField
                        label=""
                        controllerName="listPrice"
                        control={control}
                        size="small"
                        showErrorMinLength={showInlineErrorMinLength}
                        rules={{
                          required: "List Price is required.",
                          validate: {
                            validateAmount: (value: string) => {
                              return validateAmount(value, "Enter a valid List Price.");
                            },
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>

                <Grid item md={6} xs={12}>
                  <Box>
                    <FieldLabel label="Custom Price" tokens={tokens} />
                    <Box sx={inputFieldSx(!!errors.customPrice)}>
                      <CustomCurrencyField
                        label=""
                        controllerName="customPrice"
                        control={control}
                        size="small"
                      />
                    </Box>
                  </Box>
                </Grid>

                {canManageTierPrice && <Grid item xs={12}>
                  <Box
                    sx={{
                      border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                      borderRadius: "8px",
                      p: 1.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1 }}>
                      <Box>
                        <Typography fontWeight={700} sx={{ fontSize: "0.95rem", color: tokens.textPrimary }}>
                          Tier Price
                        </Typography>
                      </Box>
                      {tierRows.length < tierKeys.length && (
                        <Button
                          type="button"
                          startIcon={<AddCircleOutlineOutlined />}
                          onClick={() => openTierPopup()}
                          variant="outlined"
                          removeUpperCase
                        >
                          Add Tier Price
                        </Button>
                      )}
                    </Box>
                    {tierRows.length > 0 && (
                    <Box sx={scrollAreaSx({ maxHeight: 168, gap: 0 })}>
                    <Table
                      columns={[
                        { id: "tierLabel", label: "Tier", minWidth: 80 },
                        { id: "price", label: "Price", minWidth: 100 },
                        { id: "action", label: "Actions", minWidth: 90 },
                      ]}
                      rows={tierKeys
                        .map((tier) => tierRows.find((row) => row.tier === tier))
                        .filter((row): row is TierPriceRow => !!row)
                        .map((row) => ({ ...row, tierLabel: tierLabel(row.tier) }))}
                      disablePagination
                      actionComponent={(row: TierPriceRow) => (
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <CustomIconButton
                            color="info"
                            size="small"
                            variant="outlined"
                            tooltipMessage="Edit"
                            tooltipPlacement="top"
                            aria-label={`Edit ${row.tierLabel || row.tier}`}
                            onClick={() => openTierPopup(row)}
                          >
                            <ModeOutlined fontSize="small" />
                          </CustomIconButton>
                          <CustomIconButton
                            color="error"
                            size="small"
                            variant="outlined"
                            tooltipMessage="Delete"
                            tooltipPlacement="top"
                            aria-label={`Delete ${row.tierLabel || row.tier}`}
                            onClick={() => deleteTierRow(row.tier)}
                          >
                            <DeleteOutlineOutlined fontSize="small" />
                          </CustomIconButton>
                        </Box>
                      )}
                    />
                    </Box>
                    )}
                  </Box>
                </Grid>}

                <Grid item md={6} xs={12}>
                  <Box>
                    <FieldLabel label="Effective Date" required tokens={tokens} />
                    <Box sx={dropdownWrapSx(!!errors.effectiveDate)}>
                      <CustomDatePicker
                        label=""
                        openTo="day"
                        controllerName="effectiveDate"
                        control={control}
                        showErrorMinLength={showInlineErrorMinLength}
                        format="MM/DD/YYYY"
                        rules={{
                          required: "Effective Date is required.",
                          validate: {
                            isValidDate: (
                              value:
                                | string
                                | number
                                | Date
                                | dayjs.Dayjs
                                | null
                                | undefined
                            ) => validateEffectiveDate(value),
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item md={6} xs={12}>
                  <Box>
                    <FieldLabel label="Inactive Date" tokens={tokens} />
                    <Box sx={dropdownWrapSx(!!errors.inactiveDate)}>
                      <CustomDatePicker
                        label=""
                        openTo="day"
                        controllerName="inactiveDate"
                        control={control}
                        showErrorMinLength={showInlineErrorMinLength}
                        format="MM/DD/YYYY"
                        rules={{
                          validate: {
                            isValidDate: (
                              value:
                                | string
                                | number
                                | Date
                                | dayjs.Dayjs
                                | null
                                | undefined
                            ) => validateOptionalInactiveDate(value),
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            )}

            {watch("productPriceId") && inactiveOnly ? (
              <Grid container spacing={2}>
                <Grid container item spacing={2}>
                  <Grid item md={8} xs={12}>
                    <Box>
                      <FieldLabel label="Inactive Date" tokens={tokens} />
                      <Box sx={dropdownWrapSx(!!errors.inactiveDate)}>
                        <CustomDatePicker
                          label=""
                          openTo="day"
                          controllerName="inactiveDate"
                          control={control}
                          showErrorMinLength={showInlineErrorMinLength}
                          format="MM/DD/YYYY"
                          rules={{
                            validate: {
                              isValidDate: (
                                value:
                                  | string
                                  | number
                                  | Date
                                  | dayjs.Dayjs
                                  | null
                                  | undefined
                              ) => validateInactiveDate(value),
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            ) : null}

            <Controller
              name="fakeValidationPurposeAttribute"
              control={control}
              rules={{
                validate: { validateOthers },
              }}
              render={() => <></>}
            />
          </Box>

          <Box sx={footerSx}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box
                component="button"
                type="submit"
                onClick={() => {
                  if (editPricing) {
                    setSubmitAction("UpdateProductPrice");
                  } else {
                    setSubmitAction("AddingNewProductPricing");
                  }
                }}
                sx={{
                  ...primaryBtnSx,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.8,
                  px: 3,
                  py: 1.2,
                  border: "none",
                  cursor: "pointer",
                  color: "#fff",
                }}
              >
                <EditOutlined sx={{ fontSize: "1.1rem" }} />
                {editPricing ? "Update price" : "Add price"}
              </Box>
              <Box
                component="button"
                type="button"
                onClick={onClose}
                sx={{
                  ...cancelBtnSx,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.8,
                  px: 3,
                  py: 1.2,
                  border: "1px solid",
                  cursor: "pointer",
                }}
              >
                Cancel
              </Box>
            </Box>
          </Box>
        </form>
      </Dialog>
      <Dialog
        open={showTierPopup}
        onClose={closeTierPopup}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: paperSx(isMobile) }}
      >
        <form onSubmit={tierForm.handleSubmit(saveTierRow)}>
          <Box sx={headerSx}>
            <Box sx={{ flex: 1, pr: 4 }}>
              <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary }}>
                {editingTier ? "Edit Tier Price" : "Add Tier Price"}
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#64748b", mt: 0.15 }}>
                Select the tier and enter its price.
              </Typography>
            </Box>
            <IconButton onClick={closeTierPopup} size="small" aria-label="Close tier price">
              <X size={20} />
            </IconButton>
          </Box>
          <Box sx={bodySx}>
            <Grid container spacing={2}>
              <Grid item md={6} xs={12}>
                <FieldLabel label="Tier" required tokens={tokens} />
                <Box sx={dropdownWrapSx(!!tierForm.formState.errors.tier)}>
                  <CustomDropdownField
                    label=""
                    name="tier"
                    options={tierOptions}
                    control={tierForm.control}
                    size="small"
                    disabled={!!editingTier}
                    rules={{ required: "Tier is required." }}
                  />
                </Box>
              </Grid>
              <Grid item md={6} xs={12}>
                <FieldLabel label="Price" required tokens={tokens} />
                <Box
                  sx={{
                    ...inputFieldSx(!!tierForm.formState.errors.price),
                    "& .MuiOutlinedInput-root": {
                      height: "35px",
                      minHeight: "35px !important",
                    },
                    "& .MuiInputBase-input": {
                      height: "35px",
                      boxSizing: "border-box",
                      padding: "6px 12px !important",
                    },
                  }}
                >
                  <CustomTextField
                    control={tierForm.control}
                    controllerName="price"
                    label=""
                    size="small"
                    rules={{
                      required: "Price is required.",
                      validate: (value: string) => String(value ?? "").trim() !== "" || "Price is required.",
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
            <Box sx={{ ...footerSx, display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 2 }}>
              <Box
                component="button"
                type="button"
                onClick={tierForm.handleSubmit(saveTierRow)}
                sx={{ ...primaryBtnSx, border: "none", cursor: "pointer", color: "#fff", px: 3, py: 1.2 }}
              >
                <EditOutlined sx={{ fontSize: "1.1rem", mr: 0.8 }} />
                {editingTier ? "Update" : "Add"}
              </Box>
              <Box
                component="button"
                type="button"
                onClick={closeTierPopup}
                sx={{ ...cancelBtnSx, border: "1px solid", cursor: "pointer", px: 3, py: 1.2 }}
              >
                Cancel
              </Box>
            </Box>
          </Box>
        </form>
      </Dialog>
    </>
  );
};

export default ProductItemPricePricePopup;